import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "./api";
import { useLive } from "./live";
import type { IndicatorDef, KernelSnapshot, Mode, TemplateDef } from "./types";

export function useCatalogue() {
  return useQuery({
    queryKey: ["catalogue"],
    queryFn: () =>
      api.get<{ indicators: IndicatorDef[]; templates: TemplateDef[]; underlyings: { options: string[]; futures: string[] } }>("/api/catalogue"),
    staleTime: 10 * 60_000,
  });
}

/** The engine book for the mode being viewed (PAPER unless LIVE is chosen and exists). */
export function useKernel(): { mode: Mode; k: KernelSnapshot | undefined; hasLive: boolean } {
  const snapshot = useLive((s) => s.snapshot);
  const view = useLive((s) => s.view);
  const hasLive = !!snapshot?.kernels.LIVE;
  const mode: Mode = view === "LIVE" && hasLive ? "LIVE" : "PAPER";
  return { mode, k: snapshot?.kernels[mode], hasLive };
}

/** Re-render every ``ms`` (clocks, "x seconds ago"). */
export function useTick(ms = 1000) {
  const [, set] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => set((n) => n + 1), ms);
    return () => window.clearInterval(t);
  }, [ms]);
}

export function useLocalState<T>(key: string, initial: T): [T, (v: T | ((p: T) => T)) => void] {
  const [v, setV] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  const set = (n: T | ((p: T) => T)) =>
    setV((prev) => {
      const next = typeof n === "function" ? (n as (p: T) => T)(prev) : n;
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* storage unavailable */
      }
      return next;
    });
  return [v, set];
}

export function useTheme(): ["dark" | "light", () => void] {
  const [theme, setTheme] = useState<"dark" | "light">(() => (document.documentElement.dataset.theme === "light" ? "light" : "dark"));
  useEffect(() => {
    const obs = new MutationObserver(() => setTheme(document.documentElement.dataset.theme === "light" ? "light" : "dark"));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.dataset.theme = next;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", next === "dark" ? "#0a0b0e" : "#f3f4f7");
    try {
      localStorage.setItem("at.theme", next);
    } catch {
      /* storage unavailable */
    }
    setTheme(next);
  };
  return [theme, toggle];
}

/** Back as a phone app does it: to the previous screen when there is one in
 * this app's history, else up to ``fallback`` (a deep link opened directly). */
export function useBack(fallback: string) {
  const nav = useNavigate();
  return useCallback(() => {
    if ((window.history.state?.idx ?? 0) > 0) nav(-1);
    else nav(fallback, { replace: true });
  }, [nav, fallback]);
}

/** True while the media query matches (e.g. a phone-width screen). */
export function useMediaQuery(query: string): boolean {
  const [on, setOn] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const fn = () => setOn(m.matches);
    fn();
    m.addEventListener("change", fn);
    return () => m.removeEventListener("change", fn);
  }, [query]);
  return on;
}
